import React, { useMemo } from "react";

import * as d3 from "d3";
import { useIntl } from "react-intl";

import { Link } from "ds/Button";
import useMetricsContext from "src/organization/pages/project/environment/metrics/hooks/useMetricsContext";
import { ChartInfo } from "src/organization/pages/project/environment/metrics/http/components";
import NoData from "src/organization/pages/project/environment/metrics/http/components/nodata";
import {
  getLinearAxis,
  formatNumber,
  getDefaultDomain,
  formatPercent,
  useCursorDataSync
} from "src/organization/pages/project/environment/metrics/http/helper";
import * as S from "src/organization/pages/project/environment/metrics/http/style";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { getXTickCount } from "src/organization/pages/project/environment/metrics/utils/helper";

import {
  CpuTotalContainer,
  TitleContainer,
  Subtitle,
  InlineBlock,
  ChartTitle,
  TitleList,
  AllocationLayout
} from "../style";
import { SeriesDataType, TotalSeriesType } from "../type";
import { useUpdateTotalDimension } from "../utils";

import Area from "./Area";
import CPUTooltip from "./CpuTooltip";
import Indicator from "./Indicator";

const chartId = "total-cpu-metrics";

const getDomains = (data: TotalSeriesType, timeframe?: TimeFrameType) => {
  const flatSeries = data.flat();
  const defaultDomain = getDefaultDomain(timeframe);
  const v = d3.max(flatSeries, d => Math.max(d[0], d[1]));
  const domains = {
    xDomain: defaultDomain.xDomain,
    yDomain: [0, v]
  };

  return flatSeries.length ? domains : defaultDomain;
};

type TotalCpuProps = {
  usage?: { usage: number; allocation: number };
  data?: SeriesDataType[];
  stackInfo: {
    names: string[];
    bands: Record<string, string>;
  };
};

const TotalCPU = (props: TotalCpuProps) => {
  const intl = useIntl();

  const { data = [], stackInfo, usage } = props;
  const { timeFrame } = useMetricsContext();

  const dimension = useUpdateTotalDimension(chartId);

  const stackedSeries = useMemo(() => {
    const stack = d3.stack();
    const stackedSeries = stack.keys(stackInfo.names)(data);
    return stackedSeries;
  }, [data, stackInfo.names]);

  const { xDomain } = useMemo(
    () => getDomains(stackedSeries, timeFrame),
    [stackedSeries, timeFrame]
  );

  const { xScale, yScale, positions } = getLinearAxis({
    dimension: dimension,
    xDomain,
    yDomain: [0, usage?.allocation]
  });

  const disable = !stackedSeries.length;
  const tickX = getXTickCount(dimension.width, timeFrame);

  const cursorFuncProps = useMemo(
    () => ({
      xScale,
      yScale,
      data: stackedSeries[0],
      bisectFn: d => d.data.date,
      disable,
      chartId
    }),
    [disable, stackedSeries, xScale, yScale]
  );

  const { cursorSync, handleCursorMove } = useCursorDataSync({
    cursorFuncProps
  });
  const adaptedCursor = cursorSync?.adaptedCursor;

  return (
    <CpuTotalContainer id={chartId}>
      <TitleContainer>
        <TitleList>
          <S.ChartTitle tag="h3">CPU</S.ChartTitle>
          <ChartInfo
            link={
              <Link
                analyticId="learn_more"
                href={intl.formatMessage({
                  id: "links.documentation.understanding_metrics"
                })}
              >
                {intl.formatMessage({ id: "learn_more" })}
              </Link>
            }
            description={intl.formatMessage({
              id: "cpu_resource_allocation_info"
            })}
          />
          <InlineBlock>
            <Subtitle className="sum" tag="h3">
              {usage?.usage?.toFixed(2)}&nbsp;
            </Subtitle>
            <S.ChartTitleLight className="light">
              /&nbsp;{usage?.allocation?.toFixed(2)} CPU
            </S.ChartTitleLight>
          </InlineBlock>
        </TitleList>

        <ChartTitle className="sum" tag="h3">
          {formatPercent(usage?.usage || 0, usage?.allocation || 0)}
        </ChartTitle>
      </TitleContainer>
      <AllocationLayout
        chartId={chartId}
        yScale={yScale}
        xScale={xScale}
        positions={positions}
        tick={{
          y: {
            format: v =>
              `${formatNumber({ value: Math.min(v, usage?.allocation || 0), precision: 2 })} CPU`
          },
          x: tickX
        }}
        onMouseMove={handleCursorMove}
        onMouseLeave={() => handleCursorMove?.(null)}
        cursorData={adaptedCursor}
      >
        {disable ? (
          <NoData />
        ) : (
          <>
            {stackedSeries.map(data => (
              <Area
                bands={stackInfo.bands}
                chartId={chartId}
                key={`total_cpu_${data.key}`}
                xScale={xScale}
                yScale={yScale}
                data={data}
              />
            ))}
            {stackedSeries.map(data => (
              <Indicator
                bands={stackInfo.bands}
                chartId={chartId}
                key={`total_cpu_indicator_${data.key}`}
                xScale={xScale}
                yScale={yScale}
                data={data}
                adaptedCursor={adaptedCursor}
              />
            ))}
          </>
        )}
      </AllocationLayout>
      {adaptedCursor?.tooltipIsPositionable && (
        <CPUTooltip
          bands={stackInfo.bands}
          cursorSync={cursorSync}
          series={stackedSeries}
          total={usage?.allocation}
        />
      )}
    </CpuTotalContainer>
  );
};

export default TotalCPU;
